/**
 * Handlers para la creación de Margen Bruto Zonal (MBZ).
 *
 * Este archivo contiene la lógica de negocio para crear un nuevo MBZ,
 * incluyendo conversión de datos, manejo de errores y navegación.
 */

import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  CreateZonalGrossMarginBody,
  createZonalGrossMargin,
} from "@/services/zonal-gross-margin.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { buildZonalGrossMarginRequestBody } from "../../utils/zonal-gross-margin-converters";
import { NavigateFunction } from "react-router-dom";
import { ZonalGrossMarginCreationForm } from "./zonal-gross-margin-creation.schema";

interface CreateZonalGrossMarginHandlerProps {
  values: ZonalGrossMarginCreationForm;
  setLoadingCreate: (loading: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

/**
 * Handler para crear un nuevo Margen Bruto Zonal.
 *
 * Proceso:
 * 1. Convierte los valores del formulario al formato de la API
 * 2. Llama al endpoint POST /MargenBrutoZonal
 * 3. Muestra notificación de éxito o error
 * 4. Navega a la lista de MBZ si fue exitoso
 * 5. Maneja errores de permisos redirigiendo al login si es necesario
 *
 * @param values - Valores del formulario de creación
 * @param setLoadingCreate - Función para setear estado de loading
 * @param navigate - Función de navegación de React Router
 * @param fetchPermissions - Función para recargar permisos después de login
 * @throws Error si la creación falla (para manejo en el componente)
 */
export const createZonalGrossMarginHandler = async ({
  values,
  setLoadingCreate,
  navigate,
  fetchPermissions,
}: CreateZonalGrossMarginHandlerProps) => {
  const body: CreateZonalGrossMarginBody = buildZonalGrossMarginRequestBody(values);

  setLoadingCreate(true);
  try {
    await createZonalGrossMargin(body);

    toaster.create({
      type: "success",
      title: "Margen bruto zonal creado exitosamente.",
    });

    navigate(AppRoutes.ZONAL_GROSS_MARGINS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ZONAL_GROSS_MARGINS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se pudo crear el margen bruto zonal.",
        ),
      });
    }

    throw error;
  } finally {
    setLoadingCreate(false);
  }
};
